import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import type { TranslationGroup } from '../api/types';
import { useMutation } from '../lib/use-api';
import { languageName } from '../lib/format';
import { ErrorNotice } from './Feedback';

/**
 * Tạo bản dịch từ bản gốc của nhóm — plan §5.1b, §4.3.
 *
 * Nguồn luôn là bản `is_source_of_group`: dịch từ bản dịch là cách chắc chắn
 * nhất để sai lệch tích lũy qua từng ngôn ngữ.
 */
export function CreateTranslationDialog({
  group,
  targetLanguage,
  onClose,
  onCreated,
}: {
  group: TranslationGroup;
  targetLanguage: string;
  onClose: () => void;
  onCreated: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';
  const dialogRef = useRef<HTMLDialogElement>(null);

  const source = group.variants.find((variant) => variant.isSource) ?? group.variants[0];
  const [title, setTitle] = useState('');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const create = useMutation<{ title: string }, { contentId: string }>(
    useCallback(
      (input: { title: string }) =>
        apiFetch<{ contentId: string }>('/api/admin/translations', {
          method: 'POST',
          body: {
            sourceContentId: source?.contentId ?? '',
            targetLanguage,
            ...(input.title ? { title: input.title } : {}),
          },
        }),
      [source?.contentId, targetLanguage],
    ),
  );

  const submit = async (): Promise<void> => {
    const result = await create.run({ title: title.trim() });
    if (result) onCreated();
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <h2>{t('translation.createHeading', { title: source?.title ?? '' })}</h2>

      <p className="field-readonly">
        <span>{t('translation.targetLanguage')}</span>
        <strong>{languageName(targetLanguage, locale)}</strong>
      </p>

      <label className="field">
        <span>{t('translation.newTitle')}</span>
        <input
          type="text"
          value={title}
          placeholder={source?.title ?? ''}
          onChange={(event) => setTitle(event.target.value)}
        />
        <small className="hint">{t('translation.newTitleHint')}</small>
      </label>

      <p className="hint">{t('translation.mediaShared')}</p>

      {create.error ? <ErrorNotice error={create.error} /> : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.cancel')}
        </button>
        <button type="button" disabled={create.pending} onClick={() => void submit()}>
          {t('common:action.create')}
        </button>
      </footer>
    </dialog>
  );
}
